import React, { useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import { useHistory } from "react-router-dom";
import { Menu, MenuItem, Box, IconButton, Divider, Badge } from "@mui/material";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import { es } from "date-fns/locale";
import { Event as EventIcon } from "@mui/icons-material";
import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import SuiButton from "components/SuiButton";
import CalendarApi from "api/calendarApi";

const CalendarPreview = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [loading, setLoading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const history = useHistory();

  // Cargar eventos solo cuando se abre el menú
  useEffect(() => {
    if (anchorEl) {
      fetchUpcomingEvents();
    }
  }, [anchorEl]);

  const fetchUpcomingEvents = async () => {
    setLoading(true);
    try {
      // Cargar solo próximos 30 eventos en lugar de hacer múltiples llamadas
      const response = await CalendarApi.getUpcomingEvents(30);
      setUpcomingEvents(response.data);
    } catch (error) {
      console.error("Error fetching upcoming events:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleViewAll = () => {
    history.push("/calendario");
    handleClose();
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: "#4CAF50",
      medium: "#FFC107",
      high: "#F44336",
    };
    return colors[priority] || colors.medium;
  };

  const formatEventDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString("es-CO", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Memoizar cálculos costosos
  const todayEvents = useMemo(() => {
    return upcomingEvents.filter((event) => {
      const eventDate = new Date(event.start_time);
      const today = new Date();
      return eventDate.toDateString() === today.toDateString();
    });
  }, [upcomingEvents]);

  const selectedDateEvents = useMemo(() => {
    return upcomingEvents.filter((event) => {
      const eventDate = new Date(event.start_time);
      return eventDate.toDateString() === selectedDate.toDateString();
    });
  }, [upcomingEvents, selectedDate]);

  // Crear un Set de fechas con eventos para mejor performance
  const datesWithEvents = useMemo(() => {
    const dates = new Set();
    upcomingEvents.forEach((event) => {
      const eventDate = new Date(event.start_time);
      dates.add(eventDate.toDateString());
    });
    return dates;
  }, [upcomingEvents]);

  const CustomDay = ({ day, outsideCurrentMonth, ...other }) => {
    const hasEvent = datesWithEvents.has(day.toDateString());
    return (
      <Box position="relative">
        <PickersDay
          {...other}
          day={day}
          outsideCurrentMonth={outsideCurrentMonth}
          sx={{
            ...(hasEvent &&
              !outsideCurrentMonth && {
                position: "relative",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  bottom: 2,
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: 4,
                  height: 4,
                  borderRadius: "50%",
                  backgroundColor: "primary.main",
                },
              }),
          }}
        />
      </Box>
    );
  };

  CustomDay.propTypes = {
    day: PropTypes.instanceOf(Date).isRequired,
    outsideCurrentMonth: PropTypes.bool.isRequired,
  };

  // Handler para cambio de mes en el calendario
  const handleMonthChange = useCallback((newDate) => {
    setCurrentMonth(newDate);
    // Podríamos cargar más eventos aquí si es necesario
  }, []);

  return (
    <>
      <IconButton color="inherit" onClick={handleClick} sx={{ ml: 1 }}>
        <Badge badgeContent={todayEvents.length} color="error" max={99}>
          <EventIcon />
        </Badge>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: "visible",
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
            mt: 1.5,
            minWidth: 360,
            maxWidth: 400,
            "& .MuiList-root": {
              padding: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <SuiBox p={2}>
          <SuiTypography variant="subtitle1" fontWeight="medium">
            Calendario
          </SuiTypography>
          {todayEvents.length > 0 && (
            <SuiTypography variant="caption" textColor="secondary">
              {todayEvents.length} {todayEvents.length === 1 ? "evento" : "eventos"} hoy
            </SuiTypography>
          )}
        </SuiBox>

        <Divider />

        <SuiBox>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
            <DateCalendar
              value={selectedDate}
              onChange={setSelectedDate}
              onMonthChange={handleMonthChange}
              slots={{
                day: CustomDay,
              }}
              sx={{
                "& .MuiPickersDay-root": {
                  "&.Mui-selected": {
                    backgroundColor: "primary.main",
                  },
                },
                "& .MuiDayCalendar-weekDayLabel": {
                  color: "text.secondary",
                },
                maxWidth: "100%",
              }}
            />
          </LocalizationProvider>
        </SuiBox>

        <Divider />

        <SuiBox sx={{ maxHeight: 200, overflowY: "auto" }}>
          <SuiBox
            px={2}
            py={1}
            sx={{ position: "sticky", top: 0, bgcolor: "background.paper", zIndex: 1 }}
          >
            <SuiTypography variant="button" fontWeight="medium">
              Eventos -{" "}
              {selectedDate.toLocaleDateString("es-CO", {
                weekday: "long",
                day: "numeric",
                month: "long",
              })}
            </SuiTypography>
          </SuiBox>

          {loading ? (
            <MenuItem>
              <SuiTypography variant="button" textColor="secondary">
                Cargando eventos...
              </SuiTypography>
            </MenuItem>
          ) : selectedDateEvents.length > 0 ? (
            selectedDateEvents.map((event) => (
              <MenuItem
                key={event.id}
                sx={{
                  py: 1,
                  "&:hover": {
                    bgcolor: "action.hover",
                  },
                }}
                onClick={() => {
                  // Opcional: navegar al evento específico
                  history.push(`/calendario?event=${event.id}`);
                  handleClose();
                }}
              >
                <SuiBox width="100%">
                  <Box display="flex" alignItems="center" mb={0.5}>
                    <Box
                      sx={{
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: getPriorityColor(event.priority),
                        mr: 1,
                        flexShrink: 0,
                      }}
                    />
                    <SuiTypography
                      variant="button"
                      fontWeight="medium"
                      sx={{
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {event.title}
                    </SuiTypography>
                  </Box>
                  <SuiTypography variant="caption" textColor="secondary" sx={{ pl: 2.5 }}>
                    {formatEventDate(event.start_time)}
                    {event.location && ` • ${event.location}`}
                  </SuiTypography>
                </SuiBox>
              </MenuItem>
            ))
          ) : (
            <MenuItem>
              <SuiTypography variant="button" textColor="secondary">
                No hay eventos para este día
              </SuiTypography>
            </MenuItem>
          )}
        </SuiBox>

        <Divider />

        <SuiBox p={1}>
          <SuiButton variant="gradient" color="info" fullWidth onClick={handleViewAll} size="small">
            Ver calendario completo
          </SuiButton>
        </SuiBox>
      </Menu>
    </>
  );
};

export default CalendarPreview;
